import {
  computed,
  ref
} from "vue"

import {
  defineStore
} from "pinia"

const STORAGE_KEY =
  "hostingCheckout"

export const useHostingCartStore =
  defineStore(
    "hostingCart",
    () => {
      const plan =
        ref(null)

      const addOns =
        ref([])

      const domain =
        ref("")

      const hostingSubscriptionId =
        ref("")

      const hostingAccountId =
        ref("")

      const invoiceTransactionId =
        ref("")

      const checkoutType =
        ref("initial")

      const restored =
        ref(false)

      // ============================================================
      // CART ITEM COUNT
      // ============================================================

      const itemCount =
        computed(() => {
          let count =
            plan.value
              ? 1
              : 0

          for (
            const addon
            of addOns.value
          ) {
            count +=
              Math.max(
                1,
                Number(
                  addon.quantity ||
                  1
                )
              )
          }

          return count
        })

      // ============================================================
      // CURRENCY
      // ============================================================

      const currency =
        computed(() =>
          plan.value?.currency ||
          addOns.value?.[0]?.currency ||
          "CAD"
        )

      // ============================================================
      // PLAN PRICING
      // ============================================================

      /*
       * The original/regular plan price.
       *
       * This value must never be replaced with the discounted price
       * because it is also used for displaying the crossed-out
       * original price.
       */
      const planOriginalAmount =
        computed(() =>
          Number(
            plan.value?.price ||
            0
          )
        )

      /*
       * Discount percentage configured on the hosting plan.
       *
       * A percentage is only considered when IsDiscounted is true.
       */
      const planDiscountPercentage =
        computed(() => {
          if (
            plan.value?.isDiscounted !==
            true
          ) {
            return 0
          }

          const percentage =
            Number(
              plan.value
                ?.discountPercentage ||
              0
            )

          if (
            !Number.isFinite(
              percentage
            )
          ) {
            return 0
          }

          return Math.min(
            100,
            Math.max(
              0,
              percentage
            )
          )
        })

      /*
       * Monetary amount removed from the original plan price.
       */
      const planDiscountAmount =
        computed(() => {
          const original =
            planOriginalAmount.value

          const percentage =
            planDiscountPercentage.value

          if (
            original <= 0 ||
            percentage <= 0
          ) {
            return 0
          }

          return Number(
            (
              original *
              percentage /
              100
            ).toFixed(2)
          )
        })

      /*
       * Indicates whether the currently selected plan has
       * an active usable discount.
       */
      const planHasDiscount =
        computed(() =>
          planDiscountPercentage.value >
            0 &&
          planDiscountAmount.value >
            0
        )

      /*
       * Effective plan price used by the frontend cart.
       *
       * Priority:
       *
       * 1. No active discount:
       *      use original Price.
       *
       * 2. Discount active and backend supplied EffectivePrice:
       *      use the authoritative backend-calculated value.
       *
       * 3. Otherwise:
       *      calculate from Price and DiscountPercentage.
       *
       * The backend must still independently determine the
       * authoritative amount when creating the invoice/payment.
       */
      const planAmount =
        computed(() => {
          const original =
            planOriginalAmount.value

          if (
            !planHasDiscount.value
          ) {
            return Number(
              original.toFixed(2)
            )
          }

          const rawEffectivePrice =
            plan.value
              ?.effectivePrice

          if (
            rawEffectivePrice !==
              null &&
            rawEffectivePrice !==
              undefined &&
            rawEffectivePrice !==
              ""
          ) {
            const backendEffectivePrice =
              Number(
                rawEffectivePrice
              )

            if (
              Number.isFinite(
                backendEffectivePrice
              ) &&
              backendEffectivePrice >= 0
            ) {
              return Number(
                backendEffectivePrice
                  .toFixed(2)
              )
            }
          }

          return Number(
            (
              original -
              planDiscountAmount.value
            ).toFixed(2)
          )
        })

      // ============================================================
      // ADD-ON PRICING
      // ============================================================

      const addOnsAmount =
        computed(() => {
          let total =
            0

          for (
            const addon
            of addOns.value
          ) {
            total +=
              Number(
                addon.price ||
                0
              ) *
              Math.max(
                1,
                Number(
                  addon.quantity ||
                  1
                )
              )
          }

          return Number(
            total.toFixed(2)
          )
        })

      // ============================================================
      // CART SUBTOTAL
      // ============================================================

      /*
       * The subtotal now uses planAmount rather than plan.price.
       *
       * Therefore a discounted plan automatically affects:
       *
       * - selected plan amount
       * - cart subtotal
       * - persisted subtotal
       * - every component consuming hostingCart.subtotal
       */
      const subtotal =
        computed(() =>
          Number(
            (
              planAmount.value +
              addOnsAmount.value
            ).toFixed(2)
          )
        )

      // ============================================================
      // CART STATE
      // ============================================================

      const isEmpty =
        computed(() =>
          !plan.value &&
          addOns.value.length === 0
        )

      const hasExistingSubscription =
        computed(() =>
          Boolean(
            hostingSubscriptionId.value
          )
        )

      // ============================================================
      // QUANTITY NORMALIZATION
      // ============================================================

      function normalizeQuantity(
        addon,
        value
      ) {
        let quantity =
          Math.max(
            1,
            Number(
              value ||
              1
            )
          )

        if (
          addon?.maximumQuantity
        ) {
          quantity =
            Math.min(
              quantity,
              Number(
                addon.maximumQuantity
              )
            )
        }

        return quantity
      }

      // ============================================================
      // PLAN
      // ============================================================

      function setPlan(
        value
      ) {
        plan.value =
          value
            ? {
                ...value
              }
            : null

        persist()
      }

      // ============================================================
      // ADD-ONS
      // ============================================================

      function setAddOns(
        values
      ) {
        addOns.value =
          Array.isArray(
            values
          )
            ? values.map(
                addon => ({
                  ...addon,

                  quantity:
                    normalizeQuantity(
                      addon,
                      addon.quantity
                    )
                })
              )
            : []

        persist()
      }

      function addAddOn(
        addon
      ) {
        if (
          !addon?.id
        ) {
          return
        }

        const existing =
          addOns.value.find(
            item =>
              item.id ===
              addon.id
          )

        if (
          existing
        ) {
          existing.quantity =
            normalizeQuantity(
              existing,
              existing.quantity
            )

          persist()

          return
        }

        addOns.value.push({
          ...addon,

          quantity:
            normalizeQuantity(
              addon,
              addon.quantity
            )
        })

        persist()
      }

      function removeAddOn(
        addonId
      ) {
        addOns.value =
          addOns.value.filter(
            addon =>
              addon.id !==
              addonId
          )

        persist()
      }

      function updateAddOnQuantity(
        addonId,
        value
      ) {
        const addon =
          addOns.value.find(
            item =>
              item.id ===
              addonId
          )

        if (
          !addon
        ) {
          return
        }

        addon.quantity =
          normalizeQuantity(
            addon,
            value
          )

        persist()
      }

      // ============================================================
      // DOMAIN
      // ============================================================

      function setDomain(
        value
      ) {
        domain.value =
          String(
            value ||
            ""
          ).trim()

        persist()
      }

      // ============================================================
      // HOSTING SUBSCRIPTION
      // ============================================================

      function setHostingSubscriptionId(
        value
      ) {
        hostingSubscriptionId.value =
          String(
            value ||
            ""
          ).trim()

        checkoutType.value =
          hostingSubscriptionId.value
            ? "additional-domain"
            : "initial"

        persist()
      }

      // ============================================================
      // HOSTING ACCOUNT
      // ============================================================

      function setHostingAccountId(
        value
      ) {
        hostingAccountId.value =
          String(
            value ||
            ""
          ).trim()

        persist()
      }

      // ============================================================
      // INVOICE TRANSACTION
      // ============================================================

      function setInvoiceTransactionId(
        value
      ) {
        invoiceTransactionId.value =
          String(
            value ||
            ""
          ).trim()

        persist()
      }

      // ============================================================
      // CHECKOUT CONTEXT
      // ============================================================

      function setCheckoutContext({
        domain:
          domainValue = "",

        hostingSubscriptionId:
          subscriptionId = "",

        hostingAccountId:
          accountId = "",

        invoiceTransactionId:
          invoiceId = ""
      } = {}) {
        domain.value =
          String(
            domainValue ||
            ""
          ).trim()

        hostingSubscriptionId.value =
          String(
            subscriptionId ||
            ""
          ).trim()

        hostingAccountId.value =
          String(
            accountId ||
            ""
          ).trim()

        invoiceTransactionId.value =
          String(
            invoiceId ||
            ""
          ).trim()

        checkoutType.value =
          hostingSubscriptionId.value
            ? "additional-domain"
            : "initial"

        persist()
      }

      // ============================================================
      // REPLACE CART
      // ============================================================

      function replaceCart({
        plan:
          planValue = null,

        addOns:
          addOnValues = [],

        domain:
          domainValue = "",

        hostingSubscriptionId:
          subscriptionId = "",

        hostingAccountId:
          accountId = "",

        invoiceTransactionId:
          invoiceId = ""
      } = {}) {
        plan.value =
          planValue
            ? {
                ...planValue
              }
            : null

        addOns.value =
          Array.isArray(
            addOnValues
          )
            ? addOnValues.map(
                addon => ({
                  ...addon,

                  quantity:
                    normalizeQuantity(
                      addon,
                      addon.quantity
                    )
                })
              )
            : []

        domain.value =
          String(
            domainValue ||
            ""
          ).trim()

        hostingSubscriptionId.value =
          String(
            subscriptionId ||
            ""
          ).trim()

        hostingAccountId.value =
          String(
            accountId ||
            ""
          ).trim()

        invoiceTransactionId.value =
          String(
            invoiceId ||
            ""
          ).trim()

        checkoutType.value =
          hostingSubscriptionId.value
            ? "additional-domain"
            : "initial"

        persist()
      }

      // ============================================================
      // SESSION STORAGE
      // ============================================================

      function persist() {
        if (
          typeof window ===
          "undefined"
        ) {
          return
        }

        const snapshot = {
          planId:
            plan.value?.id ||
            null,

          /*
           * Preserve the complete plan.
           *
           * This includes:
           *
           * price
           * isDefault
           * isDiscounted
           * discountPercentage
           * effectivePrice
           * billingPeriod
           *
           * when supplied by the API.
           */
          plan:
            plan.value,

          addOns:
            addOns.value,

          // --------------------------------------------------------
          // Pricing snapshot
          // --------------------------------------------------------

          planOriginalAmount:
            planOriginalAmount.value,

          planDiscountPercentage:
            planDiscountPercentage.value,

          planDiscountAmount:
            planDiscountAmount.value,

          planHasDiscount:
            planHasDiscount.value,

          planAmount:
            planAmount.value,

          addOnsAmount:
            addOnsAmount.value,

          subtotal:
            subtotal.value,

          currency:
            currency.value,

          // --------------------------------------------------------
          // Checkout context
          // --------------------------------------------------------

          domain:
            domain.value,

          hostingSubscriptionId:
            hostingSubscriptionId.value,

          hostingAccountId:
            hostingAccountId.value,

          invoiceTransactionId:
            invoiceTransactionId.value,

          checkoutType:
            checkoutType.value
        }

        sessionStorage.setItem(
          STORAGE_KEY,
          JSON.stringify(
            snapshot
          )
        )
      }

      // ============================================================
      // RESTORE
      // ============================================================

      function restore() {
        if (
          restored.value ||
          typeof window ===
            "undefined"
        ) {
          return
        }

        restored.value =
          true

        const raw =
          sessionStorage.getItem(
            STORAGE_KEY
          )

        if (
          !raw
        ) {
          return
        }

        try {
          const saved =
            JSON.parse(
              raw
            )

          /*
           * Only restore the source data.
           *
           * Do not restore calculated pricing fields such as
           * subtotal or planAmount.
           *
           * Vue computed properties recalculate them automatically
           * from the restored plan and add-ons.
           */
          plan.value =
            saved?.plan
              ? {
                  ...saved.plan
                }
              : null

          addOns.value =
            Array.isArray(
              saved?.addOns
            )
              ? saved.addOns.map(
                  addon => ({
                    ...addon,

                    quantity:
                      normalizeQuantity(
                        addon,
                        addon.quantity
                      )
                  })
                )
              : []

          domain.value =
            String(
              saved?.domain ||
              ""
            )

          hostingSubscriptionId.value =
            String(
              saved
                ?.hostingSubscriptionId ||
              ""
            )

          hostingAccountId.value =
            String(
              saved
                ?.hostingAccountId ||
              ""
            )

          invoiceTransactionId.value =
            String(
              saved
                ?.invoiceTransactionId ||
              ""
            )

          checkoutType.value =
            hostingSubscriptionId.value
              ? "additional-domain"
              : "initial"
        }
        catch (
          error
        ) {
          console.error(
            "[HostingCart] Unable to restore cart.",
            error
          )

          clearCart()
        }
      }

      // ============================================================
      // CLEAR CART
      // ============================================================

      function clearCart() {
        plan.value =
          null

        addOns.value =
          []

        domain.value =
          ""

        hostingSubscriptionId.value =
          ""

        hostingAccountId.value =
          ""

        invoiceTransactionId.value =
          ""

        checkoutType.value =
          "initial"

        if (
          typeof window !==
          "undefined"
        ) {
          sessionStorage.removeItem(
            STORAGE_KEY
          )
        }
      }

      // ============================================================
      // PUBLIC STORE API
      // ============================================================

      return {
        plan,
        addOns,
        domain,
        hostingSubscriptionId,
        hostingAccountId,
        invoiceTransactionId,
        checkoutType,
        restored,

        itemCount,
        currency,

        // Plan pricing
        planOriginalAmount,
        planDiscountPercentage,
        planDiscountAmount,
        planHasDiscount,
        planAmount,

        // Cart pricing
        addOnsAmount,
        subtotal,

        isEmpty,
        hasExistingSubscription,

        setPlan,
        setAddOns,
        addAddOn,
        removeAddOn,
        updateAddOnQuantity,
        setDomain,
        setHostingSubscriptionId,
        setHostingAccountId,
        setInvoiceTransactionId,
        setCheckoutContext,
        replaceCart,
        persist,
        restore,
        clearCart
      }
    }
  )